import { Component } from '@theme/component';

const CACHE_TTL = 15 * 60 * 1000;

/**
 * Maps WMO weather codes (returned by Open-Meteo) to a condition label and symbol.
 */
const WEATHER_CONDITIONS = [
  { codes: [0], label: 'Clear', symbol: '☀️' },
  { codes: [1, 2], label: 'Partly cloudy', symbol: '⛅️' },
  { codes: [3], label: 'Overcast', symbol: '☁️' },
  { codes: [45, 48], label: 'Fog', symbol: '🌫️' },
  { codes: [51, 53, 55, 56, 57], label: 'Drizzle', symbol: '🌦️' },
  { codes: [61, 63, 65, 66, 67, 80, 81, 82], label: 'Rain', symbol: '🌧️' },
  { codes: [71, 73, 75, 77, 85, 86], label: 'Snow', symbol: '🌨️' },
  { codes: [95, 96, 99], label: 'Thunderstorm', symbol: '⛈️' },
];

/**
 * A custom element that displays current local weather from the Open-Meteo API.
 * Stays hidden until a forecast has been fetched successfully.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} icon - The condition symbol.
 * @property {HTMLElement} condition - The condition label.
 * @property {HTMLElement} temperature - The current temperature.
 * @property {HTMLElement} high - Today's high.
 * @property {HTMLElement} low - Today's low.
 *
 * @extends {Component<Refs>}
 */
class WeatherWidget extends Component {
  requiredRefs = ['icon', 'condition', 'temperature', 'high', 'low'];

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  async #load() {
    const latitude = this.getAttribute('latitude');
    const longitude = this.getAttribute('longitude');

    if (!latitude || !longitude) return;

    const unit = this.getAttribute('unit') === 'celsius' ? 'celsius' : 'fahrenheit';

    try {
      const forecast = await this.#fetchForecast(latitude, longitude, unit);
      this.#render(forecast);
      this.removeAttribute('hidden');
    } catch {
      // Leave the widget hidden when the forecast is unavailable.
    }
  }

  /**
   * @param {string} latitude
   * @param {string} longitude
   * @param {string} unit
   * @returns {Promise<{ current: { temperature_2m: number, weather_code: number }, daily: { temperature_2m_max: number[], temperature_2m_min: number[] } }>}
   */
  async #fetchForecast(latitude, longitude, unit) {
    const cacheKey = `weather-widget:${latitude},${longitude},${unit}`;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { expires, forecast } = JSON.parse(cached);
        if (expires > Date.now()) return forecast;
      }
    } catch {
      // Ignore unavailable or corrupted storage and fetch a fresh forecast.
    }

    const params = new URLSearchParams({
      latitude,
      longitude,
      current: 'temperature_2m,weather_code',
      daily: 'temperature_2m_max,temperature_2m_min',
      temperature_unit: unit,
      timezone: 'auto',
      forecast_days: '1',
    });

    const response = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
    if (!response.ok) throw new Error(`Weather request failed: ${response.status}`);
    const forecast = await response.json();

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ expires: Date.now() + CACHE_TTL, forecast }));
    } catch {
      // Storage may be full or unavailable; the forecast is still usable.
    }

    return forecast;
  }

  /**
   * @param {{ current: { temperature_2m: number, weather_code: number }, daily: { temperature_2m_max: number[], temperature_2m_min: number[] } }} forecast
   */
  #render(forecast) {
    const condition = WEATHER_CONDITIONS.find(({ codes }) => codes.includes(forecast.current.weather_code));

    if (condition) {
      this.refs.icon.textContent = condition.symbol;
      this.refs.condition.textContent = condition.label;
    }

    this.refs.temperature.textContent = `°${Math.round(forecast.current.temperature_2m)}`;
    this.refs.high.textContent = `↗${Math.round(forecast.daily.temperature_2m_max[0])}`;
    this.refs.low.textContent = `↘${Math.round(forecast.daily.temperature_2m_min[0])}`;
  }
}

if (!customElements.get('weather-widget')) {
  customElements.define('weather-widget', WeatherWidget);
}
